import { useMemo, useState } from "react";
import {
  api, ApiError, STAGES, STAGE_LABELS,
  type ConversionDef, type ConversionScoreMode, type ConversionSource, type ConversionTier, type ConversionValueMode,
  type FunnelStep, type ProductRow, type Stage,
} from "../api";
import { useAsync } from "../useAsync";
import { FunnelViz } from "../components/FunnelViz";
import { Sparkline } from "../components/TrendChart";
import { channelColor, channelLabel, count, delta, money, toneColor, DASH } from "../format";

const RANGES = [7, 30, 90];

/**
 * The funnel page: the funnel drawn as a funnel (current window against the
 * previous one), each step's trend over time, and the definition itself —
 * what's tracked, what's dark, edit/add/reorder steps.
 */
export function Funnel({ product }: { product: ProductRow }) {
  const slug = product.slug;
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const def = useAsync(() => api.funnelDef.get(slug), [slug]);
  // Twice the window: the second half IS the comparison baseline.
  const series = useAsync(() => api.funnelDef.series(slug, days * 2), [slug, days]);
  const channelRows = useAsync(() => api.funnel(slug, days), [slug, days]);

  const steps = def.data?.steps ?? [];

  const windows = useMemo(() => {
    const cur: Record<string, number> = {};
    const prev: Record<string, number> = {};
    if (!series.data) return { cur, prev };
    const cutoff = isoDaysAgo(days - 1); // YYYY-MM-DD compares lexicographically
    for (const r of series.data) {
      const target = r.day >= cutoff ? cur : prev;
      target[r.stage] = (target[r.stage] ?? 0) + r.volume;
    }
    return { cur, prev };
  }, [series.data, days]);

  // Weekly buckets across the full fetched range, for the per-step trends.
  const weekly = useMemo(() => {
    const buckets = Math.max(Math.floor((days * 2) / 7), 2);
    const byStage = new Map<string, number[]>();
    if (!series.data) return { buckets, byStage };
    const today = isoDaysAgo(0);
    for (const r of series.data) {
      const age = Math.floor((Date.parse(today) - Date.parse(r.day)) / 86_400_000);
      const idx = buckets - 1 - Math.min(Math.floor(age / 7), buckets - 1);
      const arr = byStage.get(r.stage) ?? new Array(buckets).fill(0);
      arr[idx] += r.volume;
      byStage.set(r.stage, arr);
    }
    return { buckets, byStage };
  }, [series.data, days]);

  if (def.error) return <PageProblem message={def.error} />;
  if (def.loading || !def.data) return <PageLoading />;

  const selectedStep = steps.find((s) => s.stage === selected) ?? null;

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Funnel</h1>
          <span className="sub">
            Last {days} days, dashed outline = the {days} days before. Click a step for its channels and sources.
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((d) => (
            <button key={d} type="button" className="seg" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
        <button type="button" className="btn-ghost" onClick={() => setEditing((v) => !v)} aria-expanded={editing}>
          {editing ? "Close editor" : "Edit steps"}
        </button>
      </div>

      {editing && (
        <StepEditor
          slug={slug}
          initial={steps}
          available={def.data.available}
          isDefault={def.data.is_default}
          onSaved={() => { setEditing(false); def.reload(); }}
          onCancel={() => setEditing(false)}
        />
      )}

      <section className="panel" style={{ marginTop: 20 }}>
        {series.error ? (
          <Problem message={series.error} />
        ) : series.loading ? (
          <Loading />
        ) : (
          <FunnelViz
            steps={steps}
            current={windows.cur}
            previous={windows.prev}
            selected={selected}
            onSelect={(s) => setSelected((v) => (v === s ? null : s))}
          />
        )}
      </section>

      {selectedStep && (
        <StepDetail
          step={selectedStep}
          cur={windows.cur[selectedStep.stage] ?? 0}
          prev={windows.prev[selectedStep.stage] ?? 0}
          channels={(channelRows.data ?? []).filter((r) => r.stage === selectedStep.stage)}
          sources={def.data.available.find((a) => a.stage === selectedStep.stage)?.sources ?? []}
          days={days}
          onClose={() => setSelected(null)}
        />
      )}

      {/* ------------------------------------------------- change over time */}
      <section className="panel" style={{ marginTop: 22 }}>
        <div className="panel-head">
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <h2>Change over time</h2>
            <span className="sub">Weekly volume per step across the last {days * 2} days, and end-to-end conversion.</span>
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column" }}>
          <TrendRow
            label="End to end"
            sub={steps.length >= 2 ? `${steps[0].label} → ${steps[steps.length - 1].label}` : ""}
            values={endToEnd(steps, weekly.byStage, weekly.buckets)}
            display={(v) => (v === null ? DASH : `${(v * 100).toFixed(2)}%`)}
            emphasis
          />
          {steps.map((s) => {
            const arr = weekly.byStage.get(s.stage) ?? new Array(weekly.buckets).fill(0);
            return (
              <TrendRow
                key={s.stage}
                label={s.label}
                sub={s.stage}
                values={arr}
                display={(v) => (v === null ? DASH : count(v))}
                deltaOf={delta(windows.cur[s.stage] ?? 0, windows.prev[s.stage] ?? 0, "up")}
              />
            );
          })}
        </div>
      </section>

      <ConversionsPanel slug={slug} currency={product.currency} />
    </div>
  );
}

function endToEnd(steps: FunnelStep[], byStage: Map<string, number[]>, buckets: number): Array<number | null> {
  if (steps.length < 2) return new Array(buckets).fill(null);
  const first = byStage.get(steps[0].stage) ?? new Array(buckets).fill(0);
  const last = byStage.get(steps[steps.length - 1].stage) ?? new Array(buckets).fill(0);
  return first.map((f, i) => (f > 0 ? last[i] / f : null));
}

function TrendRow({
  label, sub, values, display, deltaOf, emphasis,
}: {
  label: string;
  sub: string;
  values: Array<number | null>;
  display: (v: number | null) => string;
  deltaOf?: ReturnType<typeof delta>;
  emphasis?: boolean;
}) {
  const nums = values.map((v) => v ?? 0);
  const latest = values[values.length - 1] ?? null;
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "170px minmax(0,1fr) 110px 90px",
      gap: 18, alignItems: "center", padding: "9px 0",
      borderBottom: "1px solid var(--border-hairline)",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: emphasis ? 600 : 500, color: "var(--text-2)" }}>{label}</div>
        <div className="eyebrow" style={{ marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
      </div>
      <div style={{ overflow: "hidden" }}>
        <Sparkline values={nums} width={420} height={34} color={emphasis ? "var(--keeper)" : "var(--text-4)"} />
      </div>
      <div className="num" style={{ fontSize: 14, textAlign: "right" }}>{display(latest)}</div>
      <div className="num" style={{ fontSize: 12, textAlign: "right", color: deltaOf ? toneColor(deltaOf.tone) : "var(--text-faint)" }}>
        {deltaOf ? deltaOf.label : "latest wk"}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ step detail

function StepDetail({
  step, cur, prev, channels, sources, days, onClose,
}: {
  step: FunnelStep;
  cur: number;
  prev: number;
  channels: Array<{ channel: string | null; volume: number }>;
  sources: string[];
  days: number;
  onClose: () => void;
}) {
  const d = delta(cur, prev, "up");
  const merged = new Map<string, number>();
  for (const c of channels) {
    const key = c.channel ?? "unattributed";
    merged.set(key, (merged.get(key) ?? 0) + c.volume);
  }
  const rows = [...merged.entries()].sort((a, b) => b[1] - a[1]);
  const max = Math.max(...rows.map(([, v]) => v), 1);

  return (
    <section className="panel" style={{ marginTop: 14, borderColor: "var(--keeper)" }}>
      <div className="panel-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h2>{step.label}</h2>
          <span className="sub">
            {count(cur)} in the last {days}d
            {d && <> · <span style={{ color: toneColor(d.tone) }}>{d.label}</span> vs the {days}d before</>}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-text" onClick={onClose}>Close</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 28, marginTop: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>By channel</div>
          {rows.map(([ch, v]) => (
            <div key={ch} style={{ display: "grid", gridTemplateColumns: "120px minmax(0,1fr) 64px", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 12.5, color: ch === "unattributed" ? "var(--muted)" : "var(--text-2)" }}>
                {channelLabel(ch)}
              </span>
              <div style={{ height: 8, background: "var(--surface-inset)", borderRadius: 3 }}>
                <div style={{ height: "100%", width: `${(v / max) * 100}%`, borderRadius: 3,
                              background: ch === "unattributed" ? "var(--muted)" : channelColor(ch) }} />
              </div>
              <span className="num" style={{ fontSize: 12.5, textAlign: "right" }}>{count(v)}</span>
            </div>
          ))}
          {rows.length === 0 && <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>No events in this window.</span>}
        </div>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Fed by</div>
          {sources.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {sources.map((s) => (
                <span key={s} style={{ fontSize: 11.5, color: "var(--text-3)", background: "var(--surface-inset)",
                                       borderRadius: 5, padding: "4px 9px" }}>
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: 12.5, color: "var(--warn)" }}>
              Nothing writes this stage yet — it renders, but stays dark until a source is wired.
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------ step editor

function StepEditor({
  slug, initial, available, isDefault, onSaved, onCancel,
}: {
  slug: string;
  initial: FunnelStep[];
  available: Array<{ stage: Stage; volume_30d: number; events_30d: number; sources: string[] }>;
  isDefault: boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [steps, setSteps] = useState<FunnelStep[]>(() => initial.map((s) => ({ ...s })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availByStage = new Map(available.map((a) => [a.stage, a]));
  const unused = STAGES.filter((st) => !steps.some((s) => s.stage === st));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.funnelDef.save(slug, steps.map((s) => ({ stage: s.stage, label: s.label.trim() || STAGE_LABELS[s.stage] })));
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" style={{ marginTop: 20, borderColor: "var(--border-hover)" }}>
      <div className="panel-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h2>Steps &amp; tracking</h2>
          <span className="sub">
            {isDefault ? "This project is on the default ladder — make it yours. " : ""}
            Steps draw from the canonical stage taxonomy; labels are yours. Order is the funnel.
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
        {steps.map((s, i) => {
          const a = availByStage.get(s.stage);
          const dark = !a || a.events_30d === 0;
          return (
            <div key={s.stage} style={{
              display: "grid", gridTemplateColumns: "58px minmax(120px, 220px) 110px minmax(0,1fr) 32px",
              gap: 12, alignItems: "center",
            }}>
              <span style={{ display: "flex", gap: 2 }}>
                <button type="button" className="wtool" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
                <button type="button" className="wtool" onClick={() => move(i, 1)} disabled={i === steps.length - 1} title="Move down">↓</button>
              </span>
              <input className="input" style={{ minHeight: 36, fontSize: 13 }} value={s.label} maxLength={40}
                     aria-label={`Label for ${s.stage}`}
                     onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <span className="eyebrow">{s.stage}</span>
              <span style={{ fontSize: 11.5, color: dark ? "var(--warn)" : "var(--text-faint)",
                             overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dark ? "no events in 30d" : `${count(a!.volume_30d)} in 30d · ${a!.sources.join(", ")}`}
              </span>
              <button type="button" className="wtool wtool-x" title="Remove step" disabled={steps.length <= 2}
                      onClick={() => setSteps(steps.filter((_, j) => j !== i))}>×</button>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <select
          className="input" style={{ maxWidth: 260, minHeight: 38, fontSize: 13 }} value=""
          aria-label="Add step" disabled={unused.length === 0 || steps.length >= 10}
          onChange={(e) => {
            const st = e.target.value as Stage;
            if (st) setSteps([...steps, { stage: st, label: STAGE_LABELS[st] }]);
            e.target.value = "";
          }}
        >
          <option value="" disabled>{unused.length === 0 ? "All stages in use" : "Add a step…"}</option>
          {unused.map((st) => {
            const a = availByStage.get(st);
            const has = a && a.events_30d > 0;
            return (
              <option key={st} value={st}>
                {STAGE_LABELS[st]} ({st}) {has ? `— ${count(a!.volume_30d)} in 30d` : "— no data yet"}
              </option>
            );
          })}
        </select>
        <span style={{ flex: 1 }} />
        {error && <span className="form-error">{error}</span>}
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-accent" style={{ minHeight: 40 }} onClick={save}
                disabled={busy || steps.length < 2}>
          Save funnel
        </button>
      </div>
    </section>
  );
}

// ------------------------------------------------------------ conversions

const TIER_LABELS: Record<ConversionTier, string> = { primary: "Primary", secondary: "Secondary" };
const VALUE_MODE_LABELS: Record<ConversionValueMode, string> = {
  none: "Count only",
  fixed: "Fixed value",
  transaction: "From source revenue",
};
const SCORE_MODE_LABELS: Record<ConversionScoreMode, string> = {
  none: "None",
  grade: "Grade (A, B, C, D)",
  numeric: "Numeric score",
};

function ConversionsPanel({ slug, currency }: { slug: string; currency: string }) {
  const list = useAsync(() => api.conversions.list(slug), [slug]);
  const [editing, setEditing] = useState<ConversionDef | "new" | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const defs = list.data?.conversions ?? [];

  const remove = async (key: string) => {
    if (confirming !== key) { setConfirming(key); return; }
    setConfirming(null);
    setError(null);
    try {
      await api.conversions.remove(slug, key);
      list.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <section className="panel" style={{ marginTop: 22 }}>
      <div className="panel-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h2>Conversions &amp; Goals</h2>
          <span className="sub" style={{ maxWidth: 650 }}>
            The primary conversion is your bottom-of-funnel goal — waitlist signup, lead, or purchase.
            Milestones before it are secondaries. Values can be dynamic from revenue, fixed, lead-scored (A–D / points), or count-only.
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-ghost" onClick={() => setEditing("new")} disabled={editing !== null}>
          Add conversion
        </button>
      </div>

      {list.error ? (
        <Problem message={list.error} />
      ) : list.loading || !list.data ? (
        <Loading />
      ) : defs.length === 0 && editing === null ? (
        <div style={{ padding: "26px 0 8px", fontSize: 12.5, color: "var(--text-faint)" }}>
          No conversions defined yet. Add your core bottom-of-funnel goal as the primary (e.g. waitlist signup or charge), and milestones before it as secondaries.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 14 }}>
          {defs.map((d) => {
            const scoreStats = d.score_stats_30d;
            let scoreSummary: string | null = null;
            if (d.score_mode === "grade" && scoreStats && scoreStats.grades) {
              const gradeA = scoreStats.grades["A"] ?? 0;
              const totalScored = scoreStats.count_scored;
              if (totalScored > 0) {
                const pct = Math.round((gradeA / totalScored) * 100);
                scoreSummary = `${pct}% Grade A (${count(gradeA)}/${count(totalScored)})`;
              }
            } else if (d.score_mode === "numeric" && scoreStats && scoreStats.avg_score !== null) {
              scoreSummary = `Avg score: ${scoreStats.avg_score}`;
            }

            return (
              <div key={d.key} style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 1.2fr) 86px 110px minmax(100px, 1fr) 150px 140px 96px",
                gap: 14, alignItems: "center", padding: "10px 0",
                borderBottom: "1px solid var(--border-hairline)",
                opacity: d.is_active ? 1 : 0.5,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: d.tier === "primary" ? 600 : 500, color: "var(--text-2)",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {d.label}{d.is_active ? "" : " (off)"}
                  </div>
                  <div className="eyebrow" style={{ marginTop: 2 }}>{d.key}</div>
                </div>
                <span style={{ fontSize: 11.5, color: "var(--text-3)", background: "var(--surface-inset)",
                               borderRadius: 5, padding: "3px 8px", justifySelf: "start" }}>
                  {TIER_LABELS[d.tier]}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>{STAGE_LABELS[d.stage]}</div>
                  <div className="eyebrow" style={{ marginTop: 2 }}>{d.stage}</div>
                </div>
                <span style={{ fontSize: 12, color: "var(--text-4)", overflow: "hidden",
                               textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.source ? (d.event_name ? `${d.source} · ${d.event_name}` : d.source) : "any source"}
                </span>
                <div style={{ fontSize: 12, color: "var(--text-4)" }}>
                  <div>
                    {d.value_mode === "fixed"
                      ? `Fixed ${money(d.fixed_value_cents, d.currency ?? currency)}`
                      : d.score_mode !== "none"
                      ? `Scored: ${SCORE_MODE_LABELS[d.score_mode]}`
                      : VALUE_MODE_LABELS[d.value_mode]}
                  </div>
                  {d.target_cpa_cents != null && (
                    <div className="eyebrow" style={{ marginTop: 2 }}>
                      Target CPA {money(d.target_cpa_cents, currency)}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="num" style={{ fontSize: 13 }}>
                    {count(d.count_30d)} <span style={{ color: "var(--text-faint)", fontSize: 11 }}>in 30d</span>
                  </div>
                  {d.cpa_30d_cents !== null && (
                    <div className="num" style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 1 }}>
                      CPA {money(d.cpa_30d_cents, currency)}
                    </div>
                  )}
                  {scoreSummary && (
                    <div style={{ fontSize: 11, color: "var(--accent)", marginTop: 1 }}>
                      {scoreSummary}
                    </div>
                  )}
                  {d.value_30d_cents !== null && (
                    <div className="num" style={{ fontSize: 12, color: "var(--text-4)", marginTop: 1 }}>
                      {money(d.value_30d_cents, d.currency ?? currency)}
                    </div>
                  )}
                </div>
                <span style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                  <button type="button" className="btn-text" onClick={() => { setConfirming(null); setEditing(d); }}>Edit</button>
                  <button type="button" className="wtool wtool-x" title="Delete conversion"
                          style={confirming === d.key ? { width: "auto", padding: "0 8px" } : undefined}
                          onClick={() => remove(d.key)} onBlur={() => setConfirming(null)}>
                    {confirming === d.key ? "Really?" : "×"}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
      {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}

      {editing !== null && (
        <ConversionEditor
          slug={slug}
          productCurrency={currency}
          initial={editing === "new" ? null : editing}
          onSaved={() => { setEditing(null); list.reload(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function slugifyKey(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

function ConversionEditor({
  slug, productCurrency, initial, onSaved, onCancel,
}: {
  slug: string;
  productCurrency: string;
  initial: ConversionDef | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(initial?.label ?? "");
  const [key, setKey] = useState(initial?.key ?? "");
  const [keyTouched, setKeyTouched] = useState(initial !== null);
  const [tier, setTier] = useState<ConversionTier>(initial?.tier ?? "primary");
  const [stage, setStage] = useState<Stage>(initial?.stage ?? "converted");
  const [source, setSource] = useState<"" | ConversionSource>(initial?.source ?? "");
  const [eventName, setEventName] = useState(initial?.event_name ?? "");
  const [valueMode, setValueMode] = useState<ConversionValueMode>(initial?.value_mode ?? (tier === "primary" ? "none" : "none"));
  const [scoreMode, setScoreMode] = useState<ConversionScoreMode>(initial?.score_mode ?? "none");
  const [targetCpaDollars, setTargetCpaDollars] = useState(
    initial?.target_cpa_cents != null ? String(initial.target_cpa_cents / 100) : "");
  const [fixedDollars, setFixedDollars] = useState(
    initial?.fixed_value_cents != null ? String(initial.fixed_value_cents / 100) : "");
  const [fixedCurrency, setFixedCurrency] = useState(initial?.currency ?? productCurrency);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveKey = keyTouched ? key : slugifyKey(label);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const fixedCents = Math.round(Number(fixedDollars) * 100);
      const targetCpaCents = targetCpaDollars.trim() !== "" ? Math.round(Number(targetCpaDollars) * 100) : null;
      await api.conversions.save(slug, {
        key: effectiveKey,
        label: label.trim(),
        tier,
        stage,
        source: source || null,
        event_name: source === "ga4" && eventName.trim() ? eventName.trim() : null,
        value_mode: valueMode,
        fixed_value_cents: valueMode === "fixed" && Number.isFinite(fixedCents) && fixedCents > 0 ? fixedCents : null,
        currency: valueMode === "fixed" ? fixedCurrency.trim().toUpperCase() : null,
        score_mode: scoreMode,
        target_cpa_cents: targetCpaCents && Number.isFinite(targetCpaCents) && targetCpaCents > 0 ? targetCpaCents : null,
        is_active: initial?.is_active ?? true,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>
        {initial ? `Edit '${initial.label}'` : "New conversion goal"}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div className="field" style={{ minWidth: 180, flex: "1 1 180px" }}>
          <label htmlFor="cv-label">Label</label>
          <input id="cv-label" className="input" style={{ minHeight: 38, fontSize: 13 }} value={label}
                 maxLength={60} placeholder="Waitlist joined" onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 150, flex: "1 1 150px" }}>
          <label htmlFor="cv-key">Key</label>
          <input id="cv-key" className="input" style={{ minHeight: 38, fontSize: 13 }} value={effectiveKey}
                 maxLength={40} disabled={initial !== null}
                 onChange={(e) => { setKeyTouched(true); setKey(slugifyKey(e.target.value)); }} />
        </div>
        <div className="field" style={{ minWidth: 140 }}>
          <label htmlFor="cv-tier">Tier</label>
          <select id="cv-tier" className="input" style={{ minHeight: 38, fontSize: 13 }} value={tier}
                  onChange={(e) => setTier(e.target.value as ConversionTier)}>
            <option value="primary">Primary — core bottom-of-funnel goal</option>
            <option value="secondary">Secondary — milestone event</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <label htmlFor="cv-stage">Stage</label>
          <select id="cv-stage" className="input" style={{ minHeight: 38, fontSize: 13 }} value={stage}
                  onChange={(e) => setStage(e.target.value as Stage)}>
            {STAGES.map((st) => <option key={st} value={st}>{STAGE_LABELS[st]} ({st})</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 130 }}>
          <label htmlFor="cv-source">Source</label>
          <select id="cv-source" className="input" style={{ minHeight: 38, fontSize: 13 }} value={source}
                  onChange={(e) => setSource(e.target.value as "" | ConversionSource)}>
            <option value="">Any source</option>
            <option value="mysql">mysql</option>
            <option value="postgres">postgres</option>
            <option value="mongo">mongo</option>
            <option value="ga4">ga4</option>
            <option value="stripe">stripe</option>
          </select>
        </div>
        {source === "ga4" && (
          <div className="field" style={{ minWidth: 170 }}>
            <label htmlFor="cv-event">GA4 event (optional)</label>
            <input id="cv-event" className="input" style={{ minHeight: 38, fontSize: 13 }} value={eventName}
                   maxLength={120} placeholder="generate_lead" onChange={(e) => setEventName(e.target.value)} />
          </div>
        )}
        <div className="field" style={{ minWidth: 180 }}>
          <label htmlFor="cv-score-mode">Lead Scoring</label>
          <select id="cv-score-mode" className="input" style={{ minHeight: 38, fontSize: 13 }} value={scoreMode}
                  onChange={(e) => setScoreMode(e.target.value as ConversionScoreMode)}>
            <option value="none">None</option>
            <option value="grade">Categorical grade (A, B, C, D)</option>
            <option value="numeric">Continuous numeric score (points)</option>
          </select>
        </div>
        <div className="field" style={{ minWidth: 190 }}>
          <label htmlFor="cv-mode">Monetary Value</label>
          <select id="cv-mode" className="input" style={{ minHeight: 38, fontSize: 13 }} value={valueMode}
                  onChange={(e) => setValueMode(e.target.value as ConversionValueMode)}>
            <option value="none">No monetary value (count / score only)</option>
            <option value="fixed">Fixed value per conversion</option>
            <option value="transaction">From source revenue (dynamic transactions)</option>
          </select>
        </div>
        {valueMode === "fixed" && (
          <>
            <div className="field" style={{ width: 120 }}>
              <label htmlFor="cv-fixed">Amount</label>
              <input id="cv-fixed" className="input" style={{ minHeight: 38, fontSize: 13 }} type="number"
                     min="0.01" step="0.01" value={fixedDollars} placeholder="250"
                     onChange={(e) => setFixedDollars(e.target.value)} />
            </div>
            <div className="field" style={{ width: 90 }}>
              <label htmlFor="cv-cur">Currency</label>
              <input id="cv-cur" className="input" style={{ minHeight: 38, fontSize: 13 }} value={fixedCurrency}
                     maxLength={3} onChange={(e) => setFixedCurrency(e.target.value)} />
            </div>
          </>
        )}
        <div className="field" style={{ width: 140 }}>
          <label htmlFor="cv-cpa">Target CPA ({productCurrency})</label>
          <input id="cv-cpa" className="input" style={{ minHeight: 38, fontSize: 13 }} type="number"
                 min="0.01" step="0.01" value={targetCpaDollars} placeholder="5.00"
                 onChange={(e) => setTargetCpaDollars(e.target.value)} />
        </div>
      </div>
      {scoreMode !== "none" && (
        <div className="form-note" style={{ marginTop: 10 }}>
          Lead scoring enabled: FunnelKeeper will track {scoreMode === "grade" ? "qualification grades (A, B, C, D)" : "lead score points"} attached to events (via <code>fk.track</code> or API <code>score</code> field) and report channel qualification breakdowns and CPQL.
        </div>
      )}
      {valueMode === "transaction" && (
        <div className="form-note" style={{ marginTop: 10 }}>
          Dynamic values: the source writes a revenue row next to each event (e.g. from Stripe or a database revenue column), so each conversion carries its own amount.
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
        <span style={{ flex: 1 }} />
        {error && <span className="form-error">{error}</span>}
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-accent" style={{ minHeight: 40 }} onClick={save}
                disabled={busy || label.trim().length === 0 || effectiveKey.length < 2}>
          Save conversion
        </button>
      </div>
    </div>
  );
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Loading() {
  return <div style={{ padding: "40px 0", textAlign: "center", color: "var(--text-faint)", fontSize: 13 }}>Loading…</div>;
}
function Problem({ message }: { message: string }) {
  return <div style={{ padding: "28px 0", color: "var(--neg)", fontSize: 13 }}>{message}</div>;
}
function PageLoading() {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--text-faint)" }}>Loading…</div>;
}
function PageProblem({ message }: { message: string }) {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--neg)" }}>{message}</div>;
}
